"use client"

import * as React from "react"
import {
  X,
  Plus,
  Sparkles,
  Lock,
  Globe,
  Building2,
  Check,
  RefreshCw,
  Loader2,
  Settings,
  UploadCloud,
  KeyRound,
  Copy,
  Eye,
  EyeOff,
  Trash2,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { SupabaseUpload } from "@/components/SupabaseUpload"
import { AppIcon } from "@/components/desk/AppIcon"
import { App, Language } from "@/types/workspace"
import { toast } from "@/lib/toast"

interface AddCustomAppModalProps {
  isOpen: boolean
  onClose: () => void
  appToEdit?: App | null
  currentAccount?: any
  accounts?: any[]
  user?: any
  onSave: (appData: App, isEdit?: boolean) => Promise<void> | void
  t?: Record<string, string>
  lang?: Language
}

function generateRandomCode(): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
  let result = ""
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

function generateClientSecret(): string {
  const randomPart = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2)
  return `bold_cs_live_${randomPart.substring(0, 32)}`
}

function getAuthTokenFromBrowser(): string | null {
  if (typeof document === "undefined") return null
  const cookieToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token="))
    ?.split("=")[1]
  if (cookieToken) return cookieToken
  return localStorage.getItem("auth_token") || localStorage.getItem("token") || localStorage.getItem("bold_token")
}

export function AddCustomAppModal({
  isOpen,
  onClose,
  appToEdit = null,
  currentAccount,
  accounts = [],
  user,
  onSave,
  t = {},
  lang = "pt",
}: AddCustomAppModalProps) {
  const isEditMode = Boolean(appToEdit)

  const [name, setName] = React.useState("")
  const [randomCode, setRandomCode] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [iconUrl, setIconUrl] = React.useState("")
  const [iframeUrl, setIframeUrl] = React.useState("")
  const [version, setVersion] = React.useState("1.0.0.0")
  const [environment, setEnvironment] = React.useState<"Alpha" | "Beta" | "Produção">("Produção")

  // Empresas já salvas anteriormente no app (bloqueadas contra remoção no modo de edição)
  const [lockedAccountIds, setLockedAccountIds] = React.useState<string[]>([])
  // IDs de empresas adicionais selecionadas ou inseridas manualmente nesta sessão
  const [additionalAccountIds, setAdditionalAccountIds] = React.useState<string[]>([])
  const [validatedAccountNames, setValidatedAccountNames] = React.useState<Record<string, string>>({})
  const [customIdInput, setCustomIdInput] = React.useState("")
  const [isValidatingAccount, setIsValidatingAccount] = React.useState(false)

  // Credenciais
  const [clientId, setClientId] = React.useState("")
  const [clientSecret, setClientSecret] = React.useState("")
  const [showSecret, setShowSecret] = React.useState(false)
  const [copiedField, setCopiedField] = React.useState<"clientId" | "clientSecret" | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  // Inicializa valores ao abrir o modal (Modo Criação vs Modo Edição)
  React.useEffect(() => {
    if (!isOpen) return

    if (appToEdit) {
      setName(appToEdit.name || "")
      setDescription(appToEdit.description || "")
      setIconUrl(appToEdit.iconUrl || "")
      setIframeUrl(appToEdit.iframeUrl || "")
      setVersion(appToEdit.version || (appToEdit.samlAttributes as any)?.version || "1.0.0.0")
      setEnvironment((appToEdit.environment || (appToEdit.samlAttributes as any)?.environment || "Produção") as any)

      const existingAccs = Array.isArray(appToEdit.accounts)
        ? appToEdit.accounts.filter((id): id is string => Boolean(id) && typeof id === "string")
        : []
      const baseLocked = Array.from(
        new Set([
          ...(currentAccount?.id ? [String(currentAccount.id)] : []),
          ...existingAccs,
        ])
      )
      setLockedAccountIds(baseLocked)
      setAdditionalAccountIds([])
      setCustomIdInput("")
      setIsValidatingAccount(false)
      setClientId(appToEdit.clientId || (appToEdit as any).client_id || appToEdit.id || "")
      setClientSecret("")
      setShowSecret(false)
      setCopiedField(null)
      setIsSubmitting(false)
    } else {
      setName("")
      setDescription("")
      setIconUrl("")
      setIframeUrl("")
      setVersion("1.0.0.0")
      setEnvironment("Produção")
      setLockedAccountIds(currentAccount?.id ? [String(currentAccount.id)] : [])
      setAdditionalAccountIds([])
      setCustomIdInput("")
      setIsValidatingAccount(false)
      setShowSecret(false)
      setCopiedField(null)
      setIsSubmitting(false)

      setRandomCode(generateRandomCode())
      setClientId(crypto.randomUUID ? crypto.randomUUID() : `app_${Date.now()}_${generateRandomCode()}`)
      setClientSecret(generateClientSecret())
    }
  }, [isOpen, appToEdit, currentAccount?.id])

  const generatedSlug = React.useMemo(() => {
    if (isEditMode && appToEdit?.slug) {
      return appToEdit.slug
    }
    const code = randomCode || "0000"
    if (!name.trim()) return `app-${code}`
    const baseSlug = name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
    return `${baseSlug || "app"}-${code}`
  }, [isEditMode, appToEdit?.slug, name, randomCode])

  const handleRegenerateCode = () => {
    if (isEditMode) return
    setRandomCode(generateRandomCode())
  }

  const handleCopyCredential = (value: string, field: "clientId" | "clientSecret") => {
    if (!value) return
    navigator.clipboard?.writeText(value)
    setCopiedField(field)
    toast.success(field === "clientId" ? "Client ID copiado!" : "Client Secret copiado!")
    setTimeout(() => {
      setCopiedField((prev) => (prev === field ? null : prev))
    }, 2000)
  }

  const otherAvailableAccounts = React.useMemo(() => {
    if (!currentAccount?.id) return accounts
    return accounts.filter((acc) => acc.id !== currentAccount.id)
  }, [accounts, currentAccount?.id])

  const isAccountLocked = React.useCallback(
    (accId: string) => {
      const normalized = String(accId).toLowerCase().trim()
      return lockedAccountIds.some((id) => String(id).toLowerCase().trim() === normalized)
    },
    [lockedAccountIds]
  )

  const toggleAccountSelection = (accId: string) => {
    if (isAccountLocked(accId)) {
      toast.info("Esta empresa já possui acesso concedido e não pode ser removida.")
      return
    }
    setAdditionalAccountIds((prev) =>
      prev.includes(accId) ? prev.filter((id) => id !== accId) : [...prev, accId]
    )
  }

  // Valida se a Empresa (UUID) existe antes de adicionar
  const handleAddCustomAccountId = async () => {
    const trimmed = customIdInput.trim()
    if (!trimmed || isValidatingAccount) return

    if (isAccountLocked(trimmed)) {
      toast.info("Esta empresa já possui acesso ao aplicativo.")
      setCustomIdInput("")
      return
    }

    if (additionalAccountIds.some((id) => id.toLowerCase() === trimmed.toLowerCase())) {
      toast.info("Este ID de empresa já foi adicionado na lista.")
      return
    }

    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)
    if (!isUuid) {
      toast.error("Empresa não encontrada. Informe um ID (UUID) válido.")
      return
    }

    // 1. Verifica primeiro se já está nas contas conhecidas da sessão
    const localMatched = accounts.find((acc) => String(acc.id).toLowerCase() === trimmed.toLowerCase())
    if (localMatched) {
      setAdditionalAccountIds((prev) => [...prev, localMatched.id])
      setValidatedAccountNames((prev) => ({ ...prev, [localMatched.id]: localMatched.name }))
      setCustomIdInput("")
      toast.success(`Empresa "${localMatched.name}" adicionada!`)
      return
    }

    // 2. Valida no backend se a empresa realmente existe no banco
    setIsValidatingAccount(true)
    try {
      const token = getAuthTokenFromBrowser()
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query: `
            query ValidateAccount($accountId: ID!) {
              validateAccountById(accountId: $accountId) {
                exists
                id
                name
              }
            }
          `,
          variables: { accountId: trimmed },
        }),
      })

      const json = await res.json()
      const validation = json?.data?.validateAccountById

      if (validation?.exists && validation?.id) {
        const finalId = String(validation.id)
        setAdditionalAccountIds((prev) => (prev.includes(finalId) ? prev : [...prev, finalId]))
        if (validation.name) {
          setValidatedAccountNames((prev) => ({ ...prev, [finalId]: validation.name }))
        }
        setCustomIdInput("")
        toast.success(
          validation.name
            ? `Empresa "${validation.name}" validada e adicionada!`
            : "Empresa validada e adicionada com sucesso!"
        )
      } else {
        toast.error("Empresa não encontrada. Verifique se o ID informado existe.")
      }
    } catch (err) {
      toast.error("Não foi possível validar o ID da empresa. Verifique a conexão e tente novamente.")
    } finally {
      setIsValidatingAccount(false)
    }
  }

  const handleRemoveAdditionalAccountId = (idToRemove: string) => {
    if (isAccountLocked(idToRemove)) return
    setAdditionalAccountIds((prev) => prev.filter((id) => id !== idToRemove))
  }

  const lockedExternalAccountIds = React.useMemo(() => {
    return lockedAccountIds.filter(
      (id) => !currentAccount?.id || id.toLowerCase() !== String(currentAccount.id).toLowerCase()
    )
  }, [lockedAccountIds, currentAccount?.id])

  const totalAuthorizedAccounts = React.useMemo(() => {
    return Array.from(
      new Set([
        ...(currentAccount?.id ? [String(currentAccount.id)] : []),
        ...lockedAccountIds,
        ...additionalAccountIds,
      ])
    )
  }, [currentAccount?.id, lockedAccountIds, additionalAccountIds])

  const resolveAccountLabel = (id: string) => {
    const matched = accounts.find((a) => String(a.id).toLowerCase() === String(id).toLowerCase())
    if (matched?.name) return matched.name
    if (validatedAccountNames[id]) return validatedAccountNames[id]
    return id
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedName = name.trim()
    const finalIframeUrl = isEditMode ? (appToEdit?.iframeUrl || iframeUrl).trim() : iframeUrl.trim()
    const trimmedIconUrl = iconUrl.trim()

    if (!trimmedName) {
      toast.error("Por favor, preencha o nome do aplicativo.")
      return
    }

    if (!finalIframeUrl) {
      toast.error("Por favor, preencha a URL do aplicativo.")
      return
    }

    if (!finalIframeUrl.startsWith("http://") && !finalIframeUrl.startsWith("https://")) {
      toast.error("A URL do aplicativo deve começar com http:// ou https://")
      return
    }

    if (!trimmedIconUrl) {
      toast.error("Por favor, envie o arquivo de ícone do aplicativo.")
      return
    }

    const trimmedVersion = version.trim() || "1.0.0.0"
    const versionPattern = /^\d+(\.\d+){3}$/
    if (!versionPattern.test(trimmedVersion)) {
      toast.error('A versão deve estar no formato "1.0.0.0", "1.0.1.1", etc.')
      return
    }

    const finalAppId = isEditMode && appToEdit ? appToEdit.id : (crypto.randomUUID ? crypto.randomUUID() : `app_${Date.now()}`)
    const developerId =
      (isEditMode && (appToEdit?.developerId || (appToEdit as any)?.developer_id)) ||
      user?.id ||
      ""

    const appPayload: App = {
      ...(appToEdit || {}),
      id: finalAppId,
      name: trimmedName,
      slug: generatedSlug,
      description: description.trim(),
      iconUrl: trimmedIconUrl,
      iframeUrl: finalIframeUrl,
      type: appToEdit?.type || "private",
      status: appToEdit?.status || "active",
      clientId: clientId,
      ...(isEditMode ? {} : { clientSecret }),
      developerId: developerId,
      accounts: totalAuthorizedAccounts,
      scopes: appToEdit?.scopes || [],
      category: appToEdit?.category || "Personalizado",
      version: trimmedVersion,
      environment: environment,
    }

      ; (appPayload as any).developer_id = developerId
      ; (appPayload as any).client_id = clientId
    if (!isEditMode) {
      ; (appPayload as any).client_secret = clientSecret
    }

    try {
      setIsSubmitting(true)
      await onSave(appPayload, isEditMode)
      toast.success(
        isEditMode
          ? `Configurações do aplicativo "${trimmedName}" atualizadas com sucesso!`
          : `Aplicativo "${trimmedName}" criado e disponibilizado no Marketplace!`
      )
      onClose()
    } catch (err: any) {
      console.error("Erro ao salvar aplicativo:", err)
      toast.error(err?.message || "Erro ao salvar aplicativo personalizado.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-2.5 sm:p-4 font-sans">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header Compacto */}
        <div className="px-4 sm:px-5 py-3.5 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between gap-3 bg-zinc-50/70 dark:bg-zinc-900/60 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-9 rounded-xl bg-gradient-to-br from-[#004EB1] to-blue-600 text-white flex items-center justify-center shadow-sm shadow-[#004EB1]/20 shrink-0">
              {isEditMode ? <Settings className="size-4" /> : <Sparkles className="size-4" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-extrabold text-zinc-900 dark:text-white truncate">
                  {isEditMode ? "Configurar Aplicativo" : "Novo Aplicativo Personalizado"}
                </h3>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-950/30 dark:border-purple-900/40 dark:text-purple-300 shrink-0">
                  {isEditMode ? "Desenvolvedor" : "Privado"}
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                {isEditMode
                  ? "Edite nome, ícone, descrição e adicione novas empresas autorizadas."
                  : "Preencha os dados e guarde as credenciais de integração do seu app."}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="size-7 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer shrink-0"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Corpo com Scroll */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 scrollbar-thin">
          {/* Bloco 1: Ícone + Nome + Slug */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3.5 p-3 rounded-xl border border-zinc-200/80 dark:border-zinc-800 bg-zinc-50/40 dark:bg-zinc-950/40">
            {/* Upload Compacto do Ícone */}
            <div className="flex sm:flex-col items-center gap-3 sm:gap-1.5 shrink-0">
              <SupabaseUpload
                mode="custom"
                accept="image/*"
                cropShape="rounded"
                aspectRatio={1}
                currentUrl={iconUrl || null}
                onUploadSuccess={(url) => setIconUrl(url)}
                onRemove={() => setIconUrl("")}
              >
                {(trigger, uploading) => (
                  <div className="flex sm:flex-col items-center gap-3 sm:gap-1.5">
                    <button
                      type="button"
                      onClick={trigger}
                      disabled={uploading}
                      title="Clique para enviar o arquivo de ícone do aplicativo"
                      className="relative size-14 rounded-2xl border-2 border-dashed border-zinc-300 dark:border-zinc-700 hover:border-[#004EB1] dark:hover:border-blue-400 bg-white dark:bg-zinc-900 flex items-center justify-center overflow-hidden transition-all cursor-pointer group shrink-0 shadow-xs"
                    >
                      {uploading ? (
                        <Loader2 className="size-5 text-[#004EB1] animate-spin" />
                      ) : iconUrl ? (
                        <AppIcon iconUrl={iconUrl} iframeUrl={iframeUrl} name={name || "App"} size="medium" />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-zinc-400 group-hover:text-[#004EB1] transition-colors">
                          <UploadCloud className="size-5" />
                        </div>
                      )}
                    </button>
                  </div>
                )}
              </SupabaseUpload>
            </div>

            {/* Nome e Slug */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
              <div className="space-y-1 min-w-0">
                <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                  Nome do App <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: CRM Interno"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all"
                />
              </div>

              <div className="space-y-1 min-w-0">
                <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                  <Lock className="size-3 text-zinc-400" />
                  Slug
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    readOnly
                    disabled
                    value={generatedSlug}
                    className="w-full pl-3 pr-8 py-1.5 text-xs font-mono rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-800/60 text-zinc-500 dark:text-zinc-400 cursor-not-allowed truncate"
                  />
                  {!isEditMode && (
                    <button
                      type="button"
                      onClick={handleRegenerateCode}
                      title="Regerar sufixo aleatório"
                      className="absolute right-1.5 text-zinc-400 hover:text-[#004EB1] dark:hover:text-blue-400 p-1 rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors cursor-pointer"
                    >
                      <RefreshCw className="size-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bloco 2: URL do Aplicativo + Versão + Ambiente + Descrição */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
              {/* URL do Aplicativo */}
              <div className="sm:col-span-6 space-y-1">
                <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1">
                    {isEditMode ? (
                      <Lock className="size-3 text-zinc-400" />
                    ) : (
                      <Globe className="size-3 text-zinc-400" />
                    )}
                    URL do App {!isEditMode && <span className="text-red-500">*</span>}
                  </span>
                  {isEditMode && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
                      Fixo
                    </span>
                  )}
                </label>
                <input
                  type="url"
                  required
                  readOnly={isEditMode}
                  disabled={isEditMode}
                  placeholder="https://exemplo.meuapp.com.br"
                  value={iframeUrl}
                  onChange={(e) => {
                    if (!isEditMode) setIframeUrl(e.target.value)
                  }}
                  className={`w-full px-3 py-1.5 text-xs rounded-lg border font-mono transition-all ${isEditMode
                      ? "border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-800/60 text-zinc-500 dark:text-zinc-400 cursor-not-allowed"
                      : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1]"
                    }`}
                />
              </div>

              {/* Versão */}
              <div className="sm:col-span-3 space-y-1">
                <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 flex items-center justify-between">
                  <span>Versão <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="1.0.0.0"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 font-mono focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all"
                />
              </div>

              {/* Ambiente */}
              <div className="sm:col-span-3 space-y-1">
                <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300">
                  Ambiente <span className="text-red-500">*</span>
                </label>
                <select
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value as any)}
                  className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all cursor-pointer"
                >
                  <option value="Alpha">Alpha</option>
                  <option value="Beta">Beta</option>
                  <option value="Produção">Produção</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300">
                Descrição
              </label>
              <textarea
                rows={2}
                placeholder="Descreva brevemente o propósito e funcionalidades deste aplicativo..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-1.5 text-xs rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all resize-none"
              />
            </div>
          </div>

          {/* Bloco 3: Credenciais do Aplicativo (Client ID + Client Secret somente na criação) */}
          <div className="p-3 rounded-xl border border-zinc-200/80 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-950/50 space-y-2.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5">
                <KeyRound className="size-3.5 text-[#004EB1] dark:text-blue-400" />
                Credenciais do Aplicativo
              </span>
              {!isEditMode ? (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50">
                  O Client Secret é exibido somente agora na criação
                </span>
              ) : (
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
                  Client Secret oculto por segurança
                </span>
              )}
            </div>

            <div className={`grid grid-cols-1 ${!isEditMode ? "sm:grid-cols-2" : ""} gap-2.5`}>
              {/* Client ID (Sempre visível para o desenvolvedor na criação e na edição) */}
              <div className="space-y-1 min-w-0">
                <span className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 block">
                  Client ID
                </span>
                <div className="flex items-center gap-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg px-2.5 py-1.5 min-w-0">
                  <code className="text-[11px] font-mono text-zinc-700 dark:text-zinc-300 truncate flex-1 select-all">
                    {clientId}
                  </code>
                  <button
                    type="button"
                    onClick={() => handleCopyCredential(clientId, "clientId")}
                    className="text-zinc-400 hover:text-[#004EB1] dark:hover:text-blue-400 p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer shrink-0"
                    title="Copiar Client ID"
                  >
                    {copiedField === "clientId" ? (
                      <Check className="size-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Client Secret (Exibido SOMENTE na criação do aplicativo) */}
              {!isEditMode && (
                <div className="space-y-1 min-w-0">
                  <span className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 block">
                    Client Secret
                  </span>
                  <div className="flex items-center gap-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg px-2.5 py-1.5 min-w-0">
                    <code className="text-[11px] font-mono text-zinc-700 dark:text-zinc-300 truncate flex-1 select-all">
                      {showSecret ? clientSecret : "•".repeat(24)}
                    </code>
                    <button
                      type="button"
                      onClick={() => setShowSecret((prev) => !prev)}
                      className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer shrink-0"
                      title={showSecret ? "Ocultar Client Secret" : "Visualizar Client Secret"}
                    >
                      {showSecret ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyCredential(clientSecret, "clientSecret")}
                      className="text-zinc-400 hover:text-[#004EB1] dark:hover:text-blue-400 p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer shrink-0"
                      title="Copiar Client Secret"
                    >
                      {copiedField === "clientSecret" ? (
                        <Check className="size-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="size-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bloco 4: Empresas com Acesso */}
          <div className="space-y-2.5 pt-2 border-t border-zinc-150 dark:border-zinc-800">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5">
                <Building2 className="size-3.5 text-[#004EB1] dark:text-blue-400" />
                Empresas com Acesso
              </label>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 shrink-0">
                {totalAuthorizedAccounts.length} empresa(s)
              </span>
            </div>

            {/* Outras empresas do próprio usuário para clique rápido */}
            {otherAvailableAccounts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {otherAvailableAccounts.map((acc: any) => {
                  const locked = isAccountLocked(acc.id)
                  const isSelected = locked || additionalAccountIds.includes(acc.id)
                  return (
                    <button
                      key={acc.id}
                      type="button"
                      onClick={() => toggleAccountSelection(acc.id)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-semibold transition-all flex items-center gap-1.5 ${locked
                        ? "bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed"
                        : isSelected
                          ? "bg-[#004EB1] border-[#004EB1] text-white cursor-pointer"
                          : "bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-700 cursor-pointer"
                        }`}
                    >
                      <Building2 className="size-3 shrink-0" />
                      <span className="truncate max-w-[140px]">{acc.name}</span>
                      {locked ? <Lock className="size-2.5 opacity-70" /> : isSelected ? <Check className="size-3" /> : null}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Input para adicionar nova empresa por ID com validação de existência */}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Adicionar por ID da Empresa (UUID)..."
                value={customIdInput}
                onChange={(e) => setCustomIdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleAddCustomAccountId()
                  }
                }}
                className="flex-1 px-3 py-1.5 text-xs font-mono rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all min-w-0"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isValidatingAccount || !customIdInput.trim()}
                onClick={handleAddCustomAccountId}
                className="text-xs h-8 px-3 font-semibold border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer shrink-0"
              >
                {isValidatingAccount ? (
                  <>
                    <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                    <span>Validando...</span>
                  </>
                ) : (
                  <>
                    <Plus className="size-3.5 mr-1" />
                    <span>Adicionar</span>
                  </>
                )}
              </Button>
            </div>

            {/* Chips de empresas autorizadas (Empresa Atual + Fixas + Novas) */}
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-2 rounded-xl bg-zinc-50 dark:bg-zinc-950/60 border border-zinc-150 dark:border-zinc-800/80 scrollbar-thin">
              {/* Empresa Atual */}
              <span
                title={`ID: ${currentAccount?.id || ""}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-300 border border-blue-200/80 dark:border-blue-900/50"
              >
                <Building2 className="size-3 shrink-0" />
                <span className="truncate max-w-[160px]">{currentAccount?.name || "Empresa Atual"}</span>
                <Lock className="size-2.5 opacity-70 shrink-0" />
              </span>

              {/* Empresas já autorizadas anteriormente (bloqueadas contra remoção) */}
              {lockedExternalAccountIds.map((id) => (
                <span
                  key={`locked-${id}`}
                  title={`Acesso fixo • ID: ${id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-emerald-50/80 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-900/50"
                >
                  <Building2 className="size-3 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span className="truncate max-w-[170px] font-mono">{resolveAccountLabel(id)}</span>
                  <Lock className="size-2.5 text-emerald-600/70 dark:text-emerald-400/70 shrink-0" />
                </span>
              ))}

              {/* Novas empresas validadas nesta sessão */}
              {additionalAccountIds.map((id) => (
                <span
                  key={`new-${id}`}
                  title={`ID: ${id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700"
                >
                  <Building2 className="size-3 text-zinc-400 shrink-0" />
                  <span className="truncate max-w-[170px] font-mono">{resolveAccountLabel(id)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAdditionalAccountId(id)}
                    className="text-zinc-400 hover:text-red-500 transition-colors cursor-pointer shrink-0"
                    title="Remover antes de salvar"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Rodapé */}
          <div className="flex justify-end items-center gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
              className="text-xs h-8 px-3.5 font-semibold cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || !name.trim() || (!isEditMode && !iframeUrl.trim()) || !iconUrl.trim()}
              className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-4 font-semibold rounded-xl flex items-center gap-1.5 shadow-sm shadow-[#004EB1]/20 cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  <span>{isEditMode ? "Salvando..." : "Cadastrando..."}</span>
                </>
              ) : isEditMode ? (
                <>
                  <Check className="size-3.5" />
                  <span>Salvar Alterações</span>
                </>
              ) : (
                <>
                  <Plus className="size-3.5" />
                  <span>Cadastrar Aplicativo</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
